type RewardEventParameters = Record<string, string | number | boolean>

interface RewardNotification {
  kind: string
  amount: number
  data?: unknown
}
export interface RewardAnalyticsEvent {
  name: string
  parameters: RewardEventParameters
}
const creditSources = new Set([
  'ORDER',
  'CARD_PAYMENT',
  'RETURN_TO_COINS',
  'MANUAL',
  'MISSION',
  'FIRST_APP_ORDER',
  'MILESTONE',
  'BIRTHDAY',
  'STORE_PURCHASE',
  'STORE_BONUS',
  'REFERRAL_INVITEE',
  'REFERRAL_INVITER',
  'REFERRAL_BONUS',
])
const levels = new Set(['SILVER', 'GOLD', 'PLATINUM', 'DIAMOND'])

/** Only structured server facts become telemetry; display copy is never parsed. */
export function rewardEventsForNotification(
  notification: RewardNotification,
): RewardAnalyticsEvent[] {
  const data
    = notification.data && typeof notification.data === 'object'
      ? (notification.data as Record<string, unknown>)
      : {}
  const events: RewardAnalyticsEvent[] = []
  const add = (name: string, parameters: RewardEventParameters = {}) =>
    events.push({ name, parameters })
  const amount = Math.abs(notification.amount)
  const source
    = typeof data.source === 'string' ? data.source : notification.kind
  const level
    = typeof data.level === 'string' && levels.has(data.level)
      ? data.level
      : null
  if (creditSources.has(notification.kind) && amount > 0 && level) {
    add('coins_earned', { source, amount, level })
  }
  if (notification.kind === 'ACTIVATE' && amount > 0)
    add('coins_activated', { amount })
  if (notification.kind === 'EXPIRE' && amount > 0)
    add('coins_expired', { amount })
  if (['LEVEL_UP', 'LEVEL_DOWN'].includes(notification.kind) && level)
    add(notification.kind.toLowerCase(), { level })
  if (notification.kind === 'REFERRAL_REGISTERED')
    add('referral_registered')
  if (
    notification.kind === 'REFERRAL_REJECTED'
    && typeof data.reason === 'string'
  ) {
    add('referral_rejected', { reason: data.reason })
  }
  if (
    source === 'REFERRAL_INVITER'
    && (notification.kind === 'ACTIVATE' || data.status === 'AVAILABLE')
  ) {
    add('referral_converted')
  }
  if (notification.kind === 'MILESTONE' && typeof data.threshold === 'number')
    add('milestone_reached', { threshold: data.threshold, amount })
  return events
}

export interface RewardOrder {
  id: string | number
  orderPlacedAt?: string | null
  state: string
  currency: string
  total: number
  customer?: { id: string | number } | null
  verocCoins?: {
    captured: boolean
    coins: number
    discount: number
    level?: string | null
    earlyAccessHours?: number | null
  } | null
}

/** A reservation/checkbox is not spend; capture must have completed on the server. */
export function rewardSpendEvent(
  order?: RewardOrder | null,
): RewardAnalyticsEvent | null {
  if (
    !order?.orderPlacedAt
    || order.state === 'Cancelled'
    || !order.verocCoins?.captured
    || order.verocCoins.coins <= 0
  ) {
    return null
  }
  return {
    name: 'coins_spent',
    parameters: {
      amount: order.verocCoins.coins,
      discount_value: order.verocCoins.discount / 100,
      currency: order.currency,
      order_value: order.total / 100,
    },
  }
}

/** Actual pre-release eligibility is frozen on the backend's original order lot. */
export function rewardEarlyAccessEvent(
  order?: RewardOrder | null,
): RewardAnalyticsEvent | null {
  const rewards = order?.verocCoins
  if (
    !order?.orderPlacedAt
    || order.state === 'Cancelled'
    || !rewards?.level
    || !levels.has(rewards.level)
    || !(rewards.earlyAccessHours && rewards.earlyAccessHours > 0)
  ) {
    return null
  }
  return {
    name: 'early_access_purchase',
    parameters: {
      level: rewards.level,
      hours_before: rewards.earlyAccessHours,
    },
  }
}
